import React from 'react'
import PropTypes from 'prop-types'

import Dialog from 'material-ui/Dialog'

import InvoiceHeader from './InvoiceHeader'
import InvoicePreview from './InvoicePreview'
import InvoiceContent from './InvoiceContent'
import InvoiceFooter from './InvoiceFooter'
import BillingFormValidationDialog from '../common/BillingFormValidationDialog'
import NewInvoiceEditor from '../infra/NewInvoiceEditor'
import DbInvoiceEditor from '../infra/DbInvoiceEditor'
import ReferenceInfoSlider from './ReferenceInfoSlider'
import BillingItemDeletionConfirmationDialog from 'billing/common/modules/items/BillingItemDeletionConfirmationDialog'

const withInvoiceEditor = (editor, isNewInvoice) => class Container extends React.Component {

  componentDidMount() {
    editor.initialize(this.props)
  }

  componentWillUnmount() {
    editor.release()
  }

  componentWillReceiveProps(nextProps) {
    editor.release()
    editor.initialize(nextProps)
  }

  render() {
    const props = { ...this.props, editor, isNewInvoice }
    return (
      <InvoiceDialog {...props} />
    )
  }
}

export const NewInvoiceDialog = withInvoiceEditor(new NewInvoiceEditor(), true)

export const EditInvoiceDialog = withInvoiceEditor(new DbInvoiceEditor(), false)

class InvoiceDialog extends React.Component {

  static propTypes = {
    open: PropTypes.bool.isRequired,
    mode: PropTypes.string.isRequired,
    teamId: PropTypes.string.isRequired,
    invoice: PropTypes.object,
    disableActions: PropTypes.bool,
    isNewInvoice: PropTypes.bool.isRequired,
    editor: PropTypes.object.isRequired,
    onSendInvoiceSelected: PropTypes.func,
    onCloseRequested: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      open: props.open,
      mode: props.mode,
      invoiceVersion: 0,
      showInvoiceValidationDialog: false,
      showReferenceInfoSlider: false,
      openSendInvoiceDialog: false,
      showInvoiceDeletionConfirmationDialog: false,
      sendingInvoiceEmail: false,
      showInvoiceSendStatusDialog: false
    }
    this.props.editor.addValueUpdatedListener(this.onValueUpdated)
  }

  onValueUpdated = () => {
    this.setState({
      invoiceVersion: this.state.invoiceVersion + 1
    })
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.open !== this.props.open || nextProps.mode !== this.props.mode) {
      this.setState({
        open: nextProps.open,
        mode: nextProps.mode
      })
    }
  }

  handleReferenceInfoSliderBackClicked = () => {
    this.setState({
      showReferenceInfoSlider: false
    })
  }

  handleSwitchModeClicked = () => {
    this.setState({
      mode: this.state.mode === 'preview' ? 'edit' : 'preview'
    })
  }

  handleHamburgerClicked = () => {
    this.setState({
      showReferenceInfoSlider: true
    })
  }

  saveInvoice = () => {
    const saveResult = this.props.editor.save(this.props.teamId)
    if (!saveResult.success) {
      this.setState({
        showInvoiceValidationDialog: true,
        validationErrorMessage: saveResult.error
      })
    }
    return saveResult
  }

  handleSaveAndSendClicked = () => {
    const saveResult = this.saveInvoice()
    if (saveResult.success) {
      this.props.onSendInvoiceSelected(saveResult.invoice)
    }
  }

  handleSaveAsDraftClicked = () => {
    const saveResult = this.saveInvoice()
    if (saveResult.success) {
      this.props.onCloseRequested()
    }
  }

  handleDeleteClicked = () => {
    this.setState({
      showInvoiceDeletionConfirmationDialog: true
    })
  }

  handleInvoiceConfirmationDialogResult = result => {
    if (result) {
      this.props.editor.delete()
      this.props.onCloseRequested()
    }
    this.setState({
      showInvoiceDeletionConfirmationDialog: false
    })
  }

  handleInvoiceConfirmationDialogCloseRequested = () => {
    this.setState({
      showInvoiceDeletionConfirmationDialog: false
    })
  }

  handleCloseValidationDialogRequested = () => {
    this.setState({
      showInvoiceValidationDialog: false
    })
  }

  render() {
    if (!this.state.open) {
      return null
    }
    return (
      <div>
        <Dialog open={this.state.open} onRequestClose={this.props.onCloseRequested}
          bodyStyle={styles.dialogBody}
          title={
            <InvoiceHeader
              title={this.props.isNewInvoice ? 'New Invoice' : `Invoice ${this.props.editor.getNumber()}`} 
              onCloseClicked={this.props.onCloseRequested}/>
          }
          actionsContainerStyle={{ padding: 0 }}
          actions={
            <InvoiceFooter
              mode={this.state.mode}
              isNewInvoice={this.props.isNewInvoice}
              invoiceStatus={this.props.invoice && this.props.invoice.status}
              disableActions={this.props.disableActions}
              onSwitchModeClicked={this.handleSwitchModeClicked}
              onSaveAndSendClicked={this.handleSaveAndSendClicked}
              onSaveAsDraftClicked={this.handleSaveAsDraftClicked}
              onDeleteClicked={this.handleDeleteClicked} />
          }
          contentStyle={styles.dialogContent}
          autoScrollBodyContent={true}>
          <div>
            {
              this.state.mode === 'preview'
                ? <InvoicePreview
                  onBackClicked={this.handleSwitchModeClicked}
                  invoiceEditor={this.props.editor} />
                : (
                  <div>
                    <ReferenceInfoSlider
                      open={this.state.showReferenceInfoSlider}
                      onBackClicked={this.handleReferenceInfoSliderBackClicked} />
                    <InvoiceContent
                      onHamburgerClicked={this.handleHamburgerClicked}
                      invoiceVersion={this.state.invoiceVersion}
                      invoiceEditor={this.props.editor}/>
                  </div>
                )
            }
          </div>
        </Dialog>
        <BillingFormValidationDialog
          open={this.state.showInvoiceValidationDialog}
          message={this.state.validationErrorMessage}
          onCloseRequested={this.handleCloseValidationDialogRequested} />
        <BillingItemDeletionConfirmationDialog
          open={this.state.showInvoiceDeletionConfirmationDialog}
          billingEntity='invoice'
          onCloseRequested={this.handleInvoiceConfirmationDialogCloseRequested}
          onDialogResult={this.handleInvoiceConfirmationDialogResult} />
      </div>
    )
  }

}

const styles = {
  dialogContent: {
    borderRadius: 6,
    // This is a hack as there is a bug in Dialog's definition to properly
    // apply the border radius.
    overflow: 'auto',
    width: '80%',
    maxWidth: 960
  },
  dialogBody: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0
  }
}



// WEBPACK FOOTER //
// ./src/billing/invoices/InvoiceDialog.js